// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Which WOMS work order was raised for which alarm.
//
// This exists because nothing upstream records the link. AEM's alarm record has
// no work-order field (see types/alarmApi.ts), and the WOMS list endpoint cannot
// be filtered by alarm — its one correlation field, `originating_task`, is
// documented as always null in list responses. So once the browser forgets the
// reference, the work order behind a WO_CREATED alarm is unrecoverable, and
// completing the alarm without also completing the work order silently diverges
// the two systems (see AlarmNextStepCard.completeWorkOrder).
//
// Persisted rather than component state so a reload — the ordinary case — keeps
// the link. It cannot help an operator who raised the work order elsewhere; that
// case is blocked in the card instead.
//
// A stopgap, and a second hand-kept source of truth for the link: it should
// move server-side (AEM or the WOMS recording it). Until then its limits are
// known — nothing in production calls `forget`, so a link only goes when the
// MAX_REMEMBERED prune drops it; one alarm holds one work order, so raising
// another on the same alarm replaces the first; and a cleared or recreated
// alarm leaves its work order linking to a page that no longer shows it.
//
// It is per browser, not per user, so ending a session clears it
// (tokenStorage.removeUserData — logout, a 401, expiry): on a shared
// workstation the next user must not see this one's alarm labels and paths.
//
// Follows the zustand persist pattern in alarmsViewStore.ts.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface RaisedWorkOrder {
  id: string;
  /** The human-facing CMMS reference, e.g. "WO-10482". */
  reference: string;
  /** When it was raised, used only to prune the oldest entries. */
  at: number;
  /**
   * The site the order was created at, so it is read and written through the
   * site-scoped routes. Optional: entries persisted before work orders belonged
   * to a site were created through the unscoped POST and have none. Those stay
   * on the unscoped routes — a site-scoped read of a siteless order is a 404
   * upstream.
   */
  siteId?: string;
  /**
   * Detail route of the alarm that raised it, so the work order can link back.
   * Optional: entries written before this field existed have no way back.
   */
  alarmPath?: string;
  /** Short label for that link, e.g. "GEN-1-A · Low fuel pressure". */
  alarmLabel?: string;
  /**
   * An alarm-side event whose work-order write already succeeded but the
   * follow-up AEM event has not been confirmed applied — set right before
   * sending that event, cleared once it succeeds. Durable (not component
   * state) because the event's own success/error callbacks are mutate-level
   * and so never run if the card unmounts (navigation away) before AEM
   * answers: without this, remounting would read the alarm's unchanged
   * status and offer "Create work order" again, orphaning this one.
   */
  pendingEvent?: string;
}

/** Where an alarm's detail page lives, captured when the work order is raised. */
export interface RaisingAlarm {
  path: string;
  label: string;
}

/**
 * How many alarm→work-order links to keep. Nothing else ever clears this map and
 * it lives in localStorage, so a long-lived browser would otherwise grow it
 * without bound. Pruning takes the oldest first, whose alarms are long closed.
 */
export const MAX_REMEMBERED = 50;

export interface AlarmWorkOrderState {
  /** Keyed by AEM `alarm_id` — the per-occurrence id every alarm write uses. */
  byAlarmId: Record<string, RaisedWorkOrder>;
  remember: (
    _alarmId: string,
    _workOrder: { id: string; reference: string; siteId?: string },
    _alarm?: RaisingAlarm
  ) => void;
  forget: (_alarmId: string) => void;
  /** See {@link RaisedWorkOrder.pendingEvent}. No-op if the alarm has no entry. */
  setPendingEvent: (_alarmId: string, _event: string | undefined) => void;
}

/** Drop all but the newest {@link MAX_REMEMBERED} entries. */
function prune(entries: Record<string, RaisedWorkOrder>): Record<string, RaisedWorkOrder> {
  const keys = Object.keys(entries);
  if (keys.length <= MAX_REMEMBERED) return entries;
  return Object.fromEntries(
    keys
      .sort((a, b) => entries[b].at - entries[a].at)
      .slice(0, MAX_REMEMBERED)
      .map(key => [key, entries[key]])
  );
}

export const useAlarmWorkOrderStore = create<AlarmWorkOrderState>()(
  persist(
    set => ({
      byAlarmId: {},

      remember: (alarmId, workOrder, alarm) =>
        set(state => ({
          byAlarmId: prune({
            ...state.byAlarmId,
            [alarmId]: {
              id: workOrder.id,
              reference: workOrder.reference,
              siteId: workOrder.siteId,
              at: Date.now(),
              alarmPath: alarm?.path,
              alarmLabel: alarm?.label,
            },
          }),
        })),

      forget: alarmId =>
        set(state => {
          const { [alarmId]: _dropped, ...rest } = state.byAlarmId;
          return { byAlarmId: rest };
        }),

      setPendingEvent: (alarmId, event) =>
        set(state => {
          const entry = state.byAlarmId[alarmId];
          if (!entry) return state;
          return {
            byAlarmId: { ...state.byAlarmId, [alarmId]: { ...entry, pendingEvent: event } },
          };
        }),
    }),
    {
      name: 'helios_alarm_work_orders',
      version: 1,
      partialize: state => ({ byAlarmId: state.byAlarmId }),
    }
  )
);

/**
 * The work order raised for `alarmId` in this browser, or null.
 *
 * A selector rather than a raw store read so callers subscribe to one alarm's
 * entry instead of re-rendering on every other alarm's.
 */
export function selectRaisedWorkOrder(alarmId: string | undefined) {
  return (state: AlarmWorkOrderState): RaisedWorkOrder | null =>
    alarmId ? (state.byAlarmId[alarmId] ?? null) : null;
}
